import React, { useContext, useEffect, useState } from 'react';
import { Button, Chip, FormControl, Grid, MenuItem, Paper, Select, TextField, Tooltip, Typography } from '@material-ui/core';
import Backdrop from '@material-ui/core/Backdrop';
import CircularProgress from '@material-ui/core/CircularProgress';
import { makeStyles } from '@material-ui/core/styles';
import { Skeleton } from '@material-ui/lab';
import MUIDataTable from 'mui-datatables';
import { useSnackbar } from 'notistack';
import _ from 'lodash';
import moment from 'moment';
import format from 'date-fns/format';
import KeyboardArrowDownRoundedIcon from '@material-ui/icons/KeyboardArrowDownRounded';
import GetAppIcon from '@material-ui/icons/GetApp';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import AppContext from '../../Services/AppContext';
import PersistantData from '../../Services/PersistantData';
import API_Service, { axiosDefaultConfig, axiosURIencode } from '../../Services/API_Service';
import {
    defDateFormat,
    req_leave_type,
    req_leaves,
    req_leaves_export,
    req_user_list
} from '../../Helper/Constants';
import { useCommonStyles } from '../../Styles';
import LinearLoading from '../../Components/LinearLoading';
import DateRangePickerModal from '../../Components/DateRangePickerModal';
import VirtualListCB from '../../Components/VirtualListCB';
import LeaveDetailDialog from './components/LeaveDetailDialog';

const STATUS_KEYS = ['pending', 'approved', 'rejected', 'cancelled'];
const STATUS_CHIP_MAP = {
    pending:   'statusChipPending',
    approved:  'statusChipApproved',
    rejected:  'statusChipRejected',
    cancelled: 'statusChipCancelled'
};

const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing(1),
        overflow: 'hidden'
    },
    filterPaper: {
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
        borderRadius: 10,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        overflow: 'hidden'
    },
    filterBtn: {
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        height: 40,
        justifyContent: 'flex-start',
        textTransform: 'none',
        overflow: 'hidden'
    },
    filterBtnText: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        width: '100%',
        textAlign: 'left'
    },
    statusChip: {
        fontWeight: 500,
        fontSize: 12,
        borderRadius: 6,
        height: 24
    },
    statusChipPending: {
        backgroundColor: 'rgba(230, 81, 0, 0.10)',
        color: '#ffb74d'
    },
    statusChipApproved: {
        backgroundColor: 'rgba(46, 125, 50, 0.12)',
        color: '#66bb6a'
    },
    statusChipRejected: {
        backgroundColor: 'rgba(211, 47, 47, 0.12)',
        color: '#ef5350'
    },
    statusChipCancelled: {
        backgroundColor: 'rgba(120, 120, 120, 0.12)',
        color: '#9e9e9e'
    },
    actionRow: {
        display: 'flex',
        gap: theme.spacing(1),
        justifyContent: 'flex-end'
    },
    skeletonCard: {
        borderRadius: 10
    },
    skeletonFilter: {
        borderRadius: 10,
        marginTop: theme.spacing(2)
    },
    skeletonTable: {
        borderRadius: 10,
        marginTop: theme.spacing(2)
    },
    tableWrapper: {
        marginTop: theme.spacing(1)
    },
    backdrop: {
        zIndex: theme.zIndex.drawer + 1,
        color: '#fff',
        backdropFilter: 'blur(4px)',
        backgroundColor: 'rgba(0, 0, 0, 0.6)'
    },
    backdropBox: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: theme.spacing(2)
    }
}));

export default function Leaves() {
    const classes = useStyles();
    const commonStyle = useCommonStyles();
    const singleton = PersistantData.getInstance();
    const [gState] = useContext(AppContext);
    var { translation: language, client } = gState;
    if (typeof (language) === 'undefined') {
        language = singleton.getlang();
    }

    const { enqueueSnackbar } = useSnackbar();

    const formatStatus = (raw) => {
        const t = (language && language[raw]) || raw;
        if (/^[A-Z]+$/.test(t)) {
            return t.charAt(0) + t.slice(1).toLowerCase();
        }
        return t;
    };

    const [loading, setLoading] = useState(true);
    const [tableLoading, setTableLoading] = useState(false);
    const [exporting, setExporting] = useState(false);

    const [agentList, setAgentList] = useState([]);
    const [typeList, setTypeList] = useState([]);
    const [leaveList, setLeaveList] = useState([]);

    const [selectedAgent, setSelectedAgent] = useState('');
    const [selectedAgentName, setSelectedAgentName] = useState('');
    const [agentView, setAgentView] = useState(false);

    const [selectedType, setSelectedType] = useState('');
    const [selectedTypeName, setSelectedTypeName] = useState('');
    const [typeView, setTypeView] = useState(false);

    const [selectedStatus, setSelectedStatus] = useState('');
    const [applyDate, setApplyDate] = useState('');
    const [dateRange, setDateRange] = useState({});
    const [openDatePicker, setOpenDatePicker] = useState(false);

    const [tableVisible, setTableVisible] = useState(false);
    const [detailOpen, setDetailOpen] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(0);

    useEffect(() => {
        if (client) {
            loadAgents();
            loadLeaveTypes();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [client]);

    const snack = (msg, type = 'success') => {
        enqueueSnackbar(msg, {
            variant: type,
            preventDuplicate: true,
            autoHideDuration: type === 'success' ? 2000 : 3000
        });
    };

    const loadAgents = () => {
        var parameters = '';
        if (gState.company) {
            const param = axiosURIencode({ company: gState.company });
            parameters = `?${param}`;
        }
        API_Service.get(`${req_user_list}${parameters}`, axiosDefaultConfig(client)).then(({ data }) => {
            const { result, extraData } = data;
            if (parseInt(result) === 1 && Array.isArray(extraData)) {
                const arr = extraData.map((u) => ({
                    name: u.name || u.username || ('User #' + u.login_id),
                    secondary: u.staff_code || u.username || '',
                    value: u.login_id,
                    status: parseInt(u.login_status) === 1 ? 1 : 0
                }));
                setAgentList(arr);
            }
            setLoading(false);
        }).catch(() => {
            snack('Network error', 'error');
            setLoading(false);
        });
    };

    const loadLeaveTypes = () => {
        API_Service.get(req_leave_type, axiosDefaultConfig(client)).then(({ data }) => {
            const { result, extraData } = data;
            if (parseInt(result) === 1) {
                const arr = (extraData || []).map((t) => ({
                    name: parseInt(t.status) === 1 ? t.lt_name : t.lt_name + ' (' + (language.inactive || 'Inactive') + ')',
                    secondary: t.lt_code,
                    value: t.lt_code,
                    status: parseInt(t.status)
                }));
                setTypeList(arr);
            }
        }).catch(() => snack('Network error', 'error'));
    };

    const handleAgentClose = (data) => {
        setAgentView(false);
        if (data && data.length > 0) {
            setSelectedAgent(data.map((d) => d.value).join(','));
            if (data.length === 1) {
                setSelectedAgentName(data[0].name);
            } else {
                setSelectedAgentName(`${data.length} ${language.agent} ${language.selected}`);
            }
        } else if (data && data.value === '') {
            setSelectedAgent('');
            setSelectedAgentName('');
        }
    };

    const handleTypeClose = (data) => {
        setTypeView(false);
        if (data && data.length > 0) {
            setSelectedType(data.map((d) => d.value).join(','));
            if (data.length === 1) {
                setSelectedTypeName(data[0].name);
            } else {
                setSelectedTypeName(`${data.length} ${language.leave_type} ${language.selected}`);
            }
        } else if (data && data.value === '') {
            setSelectedType('');
            setSelectedTypeName('');
        }
    };

    const buildFilterFormData = () => {
        var st_date = '';
        var st_date_to = '';
        if (!_.isEmpty(dateRange)) {
            st_date = format(dateRange.startDate, defDateFormat);
            st_date_to = format(dateRange.endDate, defDateFormat);
        }
        const formdata = new FormData();
        if (selectedAgent) formdata.append('salesperson_id', selectedAgent);
        if (selectedType) formdata.append('lt_code', selectedType);
        if (selectedStatus) formdata.append('status', selectedStatus);
        if (applyDate) formdata.append('apply_date', applyDate);
        if (st_date) formdata.append('date_from', st_date);
        if (st_date_to) formdata.append('date_to', st_date_to);
        return formdata;
    };

    const handleView = () => {
        const formdata = buildFilterFormData();
        setTableLoading(true);
        setTableVisible(true);
        API_Service.post(req_leaves, formdata, axiosDefaultConfig(client)).then(({ data }) => {
            if (parseInt(data.result) === 1) {
                setLeaveList(data.extraData || []);
            } else {
                setLeaveList([]);
                snack(data.message || language.no_data, 'warning');
            }
            setTableLoading(false);
        }).catch(() => {
            snack('Network error', 'error');
            setTableLoading(false);
        });
    };

    const handleExport = () => {
        const formdata = buildFilterFormData();
        setExporting(true);
        API_Service.post(req_leaves_export, formdata, axiosDefaultConfig(client)).then(({ data }) => {
            if (parseInt(data.result) === 1 && data.extraData && data.extraData.url) {
                const link = document.createElement('a');
                link.href = data.extraData.url;
                link.download = data.extraData.filename || 'Leaves_Report.xlsx';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                snack(data.message || 'Excel report downloaded');
            } else {
                snack(data.message || 'Export failed', 'error');
            }
            setExporting(false);
        }).catch(() => {
            snack('Network error', 'error');
            setExporting(false);
        });
    };

    const getDateDisplay = () => {
        if (!_.isEmpty(dateRange)) {
            return moment(dateRange.startDate).format('DD MMM YYYY') + ' / ' + moment(dateRange.endDate).format('DD MMM YYYY');
        }
        return language.date_range || 'Date Range';
    };

    const columns = [
        {
            name: 'created_at',
            label: language.apply_date,
            options: {
                customBodyRender: (value) => value ? moment(value, 'YYYY-MM-DD HH:mm:ss').format('DD/MM/YYYY') : ''
            }
        },
        {
            name: 'agent_name',
            label: language.agent
        },
        {
            name: 'leave_from',
            label: language.leave_from,
            options: {
                customBodyRender: (value) => value ? moment(value).format('DD/MM/YYYY') : ''
            }
        },
        {
            name: 'leave_to',
            label: language.leave_to,
            options: {
                customBodyRender: (value) => value ? moment(value).format('DD/MM/YYYY') : ''
            }
        },
        {
            name: 'leave_days',
            label: language.leave_days
        },
        {
            name: 'leave_type_name',
            label: language.type,
            options: {
                customBodyRender: (value, tableMeta) => value || leaveList[tableMeta.rowIndex]?.lt_code || ''
            }
        },
        {
            name: 'status',
            label: language.status,
            options: {
                customBodyRender: (value) => {
                    const chipClass = STATUS_CHIP_MAP[value] || '';
                    return (
                        <Chip
                            size="small"
                            label={formatStatus(value)}
                            className={`${classes.statusChip} ${chipClass ? classes[chipClass] : ''}`}
                        />
                    );
                }
            }
        },
        {
            name: 'approved_by',
            label: language.approved_by
        },
        {
            name: 'approved_at',
            label: language.approved_date,
            options: {
                customBodyRender: (value) => value ? moment(value).format('DD/MM/YYYY') : ''
            }
        },
        {
            name: 'rejected_by',
            label: language.rejected_by
        },
        {
            name: 'rejected_at',
            label: language.rejected_date,
            options: {
                customBodyRender: (value) => value ? moment(value).format('DD/MM/YYYY') : ''
            }
        },
        {
            name: 'leave_attachment',
            label: language.attachments,
            options: {
                customBodyRender: (value) => {
                    const arr = Array.isArray(value) ? value : [];
                    if (arr.length === 0) return '';
                    return (
                        <Tooltip title={`${arr.length} ${language.attachments}`}>
                            <AttachFileIcon fontSize="small" color="action" />
                        </Tooltip>
                    );
                }
            }
        }
    ];

    const options = {
        selectableRows: 'none',
        responsive: 'standard',
        rowsPerPage: 20,
        rowsPerPageOptions: [20, 50, 100],
        download: false,
        print: false,
        filter: false,
        search: true,
        onRowClick: (rowData, rowMeta) => {
            setSelectedIndex(rowMeta.dataIndex);
            setDetailOpen(true);
        }
    };

    if (loading) {
        return (
            <div className={classes.root}>
                <Skeleton animation="wave" height={60} className={classes.skeletonFilter} />
                <Skeleton animation="wave" height={300} className={classes.skeletonTable} />
            </div>
        );
    }

    return (
        <div className={classes.root}>
            <Paper variant="outlined" className={classes.filterPaper}>
                <Grid container spacing={1} alignItems="center">
                    <Grid item xs={12} sm={2}>
                        <Button
                            className={classes.filterBtn}
                            variant="outlined"
                            size="small"
                            onClick={() => setAgentView(true)}
                            endIcon={<KeyboardArrowDownRoundedIcon />}>
                            <Typography className={`${commonStyle.searchBtnFont} ${classes.filterBtnText}`}>
                                {selectedAgentName || language.agent}
                            </Typography>
                        </Button>
                    </Grid>

                    <Grid item xs={12} sm={2}>
                        <Button
                            className={classes.filterBtn}
                            variant="outlined"
                            size="small"
                            onClick={() => setTypeView(true)}
                            endIcon={<KeyboardArrowDownRoundedIcon />}>
                            <Typography className={`${commonStyle.searchBtnFont} ${classes.filterBtnText}`}>
                                {selectedTypeName || language.leave_type}
                            </Typography>
                        </Button>
                    </Grid>

                    <Grid item xs={12} sm={2}>
                        <FormControl variant="outlined" size="small" fullWidth>
                            <Select
                                value={selectedStatus}
                                onChange={(e) => setSelectedStatus(e.target.value)}
                                displayEmpty
                            >
                                <MenuItem value="">{language.status}</MenuItem>
                                {STATUS_KEYS.map((s) => (
                                    <MenuItem key={s} value={s}>{formatStatus(s)}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={2}>
                        <TextField
                            type="date"
                            size="small"
                            variant="outlined"
                            fullWidth
                            label={language.apply_date}
                            InputLabelProps={{ shrink: true }}
                            value={applyDate}
                            onChange={(e) => setApplyDate(e.target.value)}
                        />
                    </Grid>

                    <Grid item xs={12} sm={2}>
                        <Button
                            className={classes.filterBtn}
                            variant="outlined"
                            size="small"
                            onClick={() => setOpenDatePicker(true)}>
                            <Typography className={`${commonStyle.searchBtnFont} ${classes.filterBtnText}`}>
                                {getDateDisplay()}
                            </Typography>
                        </Button>
                    </Grid>

                    <Grid item xs={12} sm={2}>
                        <div className={classes.actionRow}>
                            <Button
                                size="small"
                                color="primary"
                                variant="contained"
                                onClick={handleView}
                            >
                                {language.view}
                            </Button>
                            <Button
                                size="small"
                                variant="contained"
                                onClick={handleExport}
                                startIcon={<GetAppIcon />}
                                disabled={exporting}
                            >
                                {language.generate_report}
                            </Button>
                        </div>
                    </Grid>
                </Grid>
            </Paper>

            {tableLoading && <LinearLoading />}
            {tableVisible && (
                <div className={classes.tableWrapper}>
                    <MUIDataTable
                        title={language.leaves_listing}
                        data={leaveList}
                        columns={columns}
                        options={options}
                    />
                </div>
            )}

            <VirtualListCB
                open={agentView}
                setClose={handleAgentClose}
                title={language.agent}
                data={agentList}
                language={language}
                filteration={1}
            />

            <VirtualListCB
                open={typeView}
                setClose={handleTypeClose}
                title={language.leave_type}
                data={typeList}
                language={language}
                filteration={1}
            />

            <DateRangePickerModal
                setPositive={(range) => {
                    setDateRange(range);
                    setOpenDatePicker(false);
                }}
                setNegative={() => setOpenDatePicker(false)}
                setClose={() => setOpenDatePicker(false)}
                open={openDatePicker}
                message={language.date_range}
                positive={language.ok}
            />

            <LeaveDetailDialog
                open={detailOpen}
                onClose={() => setDetailOpen(false)}
                leaveList={leaveList}
                selectedIndex={selectedIndex}
                onStatusUpdated={handleView}
            />

            <Backdrop className={classes.backdrop} open={exporting}>
                <div className={classes.backdropBox}>
                    <CircularProgress color="inherit" size={56} />
                    <Typography variant="h6">{language.generating_report_loading || 'Generating Report...'}</Typography>
                </div>
            </Backdrop>
        </div>
    );
}
